// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import type { MoveUploadResult } from '@/asset/api/upload-move-data';
import type { ExpandedSectionsState } from '@/asset/components/move-upload/types';
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNewRounded,
} from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Link,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { RegisteredRouter, useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

// ErrorCodeMoveImportDBCollision from constants/errors.go
const MOVE_COLLISION_ERROR_CODE = 3513;
// Matches "(asset: assetKey, move: aegKey)" in the collision error message
const COLLISION_KEY_REGEX = /\(asset: ([^,]+), move: ([^)]+)\)/;

// Builds the deep link href for an asset's event group
function buildAEGLink(
  router: RegisteredRouter,
  assetKey: string,
  aegKey: string
): string {
  return router.buildLocation({
    to: '/assets/$assetKey',
    params: { assetKey },
    search: { editEventGroup: aegKey },
  }).href;
}

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: MoveUploadResult | null;
  expandedSections: ExpandedSectionsState;
  onToggleExpanded: (section: keyof ExpandedSectionsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowError {
  message: string;
  errorCode: number;
}

interface RowFailure {
  rowNumber: number;
  serial: string;
  errors: RowError[];
}

interface SucceededMove {
  serial: string;
  index: number;
  assetKey: string;
  aegKey: string;
  originName: string;
  destinationName: string;
}

interface SucceededMoveItemData {
  moves: SucceededMove[];
  router: RegisteredRouter;
}

interface SucceededMoveRowProps {
  index: number;
  style: React.CSSProperties;
  data: SucceededMoveItemData;
}

const SucceededMoveRow = (props: SucceededMoveRowProps) => {
  const item = props.data.moves[props.index];
  const href = buildAEGLink(props.data.router, item.assetKey, item.aegKey);

  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {item.originName} to {item.destinationName}
              <OpenInNewRounded sx={{ fontSize: 14 }} />
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemData {
  rows: RowFailure[];
  router: RegisteredRouter;
}

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: FailedRowItemData;
}

const FailedRowItem = (props: FailedRowItemProps) => {
  const row = props.data.rows[props.index];
  const router = props.data.router;

  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${String(row.rowNumber)}${row.serial ? ` (serial "${row.serial}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((rowError, idx) => {
                if (rowError.errorCode === MOVE_COLLISION_ERROR_CODE) {
                  const match = COLLISION_KEY_REGEX.exec(rowError.message);
                  if (match) {
                    const href = buildAEGLink(router, match[1], match[2]);
                    return (
                      <li key={idx}>
                        This move collides with an existing{' '}
                        <Link
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          move
                          <OpenInNewRounded sx={{ fontSize: 14 }} />
                        </Link>
                      </li>
                    );
                  }
                }
                return <li key={idx}>{rowError.message}</li>;
              })}
            </ul>
          }
        />
      </ListItem>
    </div>
  );
};

export function Results(props: ResultsProps) {
  const router = useRouter();
  const [isCopied, setIsCopied] = useState(false);

  const processedData = useMemo(() => {
    if (!props.uploadResult) {
      return {
        succeededMoves: [],
        failedRows: [],
      };
    }

    const succeededMoves: SucceededMove[] = [];
    const failedRows: RowFailure[] = [];

    // Process items (successful moves and item-level errors)
    props.uploadResult.items.forEach((item) => {
      if (item.errors.length > 0) {
        failedRows.push({
          rowNumber: item.index,
          serial: item.serial,
          errors: item.errors.map((err) => ({
            message: `Column "${err.fieldName}": ${err.error}`,
            errorCode: err.errorCode,
          })),
        });
        return;
      }
      succeededMoves.push({
        serial: item.serial,
        index: item.index,
        assetKey: item.assetKey,
        aegKey: item.aegKey,
        originName: item.originName,
        destinationName: item.destinationName,
      });
    });

    // Process rowErrors (validation errors grouped by row)
    const rowErrorMap = new Map<number, RowError[]>();
    props.uploadResult.rowErrors.forEach((rowError) => {
      const existing = rowErrorMap.get(rowError.index) ?? [];
      const newErrors = rowError.errors.map((err) => ({
        message: `Column "${err.fieldName}": ${err.error}`,
        errorCode: err.errorCode,
      }));
      rowErrorMap.set(rowError.index, [...existing, ...newErrors]);
    });

    // Convert consolidated row errors to RowFailure format
    rowErrorMap.forEach((errors, rowNumber) => {
      failedRows.push({
        rowNumber,
        serial: '',
        errors,
      });
    });

    // Sort failed rows by row number
    failedRows.sort((a, b) => a.rowNumber - b.rowNumber);

    return { succeededMoves, failedRows };
  }, [props.uploadResult]);

  const { succeededMoves, failedRows } = processedData;

  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const copyRows = failedRows.map((row) => ({
        ...row,
        errors: row.errors.map((err) => err.message),
      }));
      const text = formatFailedRowsForCopy(copyRows, (row) =>
        createIdentifierSuffix(row, 'serial', 'serial')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, structureErrors } = props.uploadResult;

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                All{' '}
                {pluralize(succeededMoves.length, {
                  singular: 'move',
                  plural: 'moves',
                })}{' '}
                imported successfully
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                  cursor: succeededMoves.length ? 'pointer' : undefined,
                }}
                onClick={
                  succeededMoves.length
                    ? () => props.onToggleExpanded('succeeded')
                    : undefined
                }
              >
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Moves imported ({formatNumber(succeededMoves.length)})
                  </Typography>
                </Box>
                {succeededMoves.length > 0 &&
                  (props.expandedSections.succeeded ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  ))}
              </Box>
              <Collapse in={props.expandedSections.succeeded}>
                <VirtualList
                  height={Math.min(succeededMoves.length * ITEM_HEIGHT, 200)}
                  itemCount={succeededMoves.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={{ moves: succeededMoves, router }}
                  width="100%"
                >
                  {SucceededMoveRow}
                </VirtualList>
              </Collapse>
            </Paper>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 ? (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required move import fields
                  </Typography>
                </>
              ) : (
                <>
                  <AlertTitle>Import failed</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that could not be used to
                    import moves
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {props.expandedSections.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedSections.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        {row.error}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'move',
                        plural: 'moves',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedSections.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedSections.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={{ rows: failedRows, router }}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
